import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  LogOut, Mail, Wallet as WalletIcon, TrendingUp, Shield, Users, Crown, ChevronRight, ShieldAlert,
} from "lucide-react";
import { Header } from "@/components/Header";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import AndroidDownload from "@/components/AndroidDownload";

const TIER_COLORS = {
  Rookie: "#94A3B8", Bronze: "#CD7F32", Silver: "#CBD5E1",
  Gold: "#F5B301", Platinum: "#7DD3FC", Diamond: "#00E676",
};

export default function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (!user || typeof user !== "object") return null;

  const stats = [
    { label: "Balance", value: `${user.balance.toFixed(2)} USDT`, icon: WalletIcon, color: "text-[#00E676]" },
    { label: "Deposited", value: `${user.total_deposited.toFixed(2)} USDT`, icon: TrendingUp, color: "text-slate-200" },
    {
      label: "Trading Profit",
      value: `${user.earned_profit >= 0 ? "+" : ""}${user.earned_profit.toFixed(2)} USDT`,
      icon: TrendingUp,
      color: user.earned_profit >= 0 ? "text-[#00E676]" : "text-[#FF3B30]",
    },
  ];

  return (
    <div className="pb-24 grain min-h-screen">
      <Header title="Account" />
      <div className="max-w-md mx-auto px-4 pt-5 space-y-4">
        <AndroidDownload />
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl border border-[#1E293B] bg-[#121721] p-5 flex items-center gap-4"
        >
          <div className="w-14 h-14 rounded-2xl bg-[#00E676] flex items-center justify-center text-black font-display font-bold text-xl">
            {(user.name || user.email)[0].toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="font-display font-bold text-lg truncate">{user.name || "Trader"}</div>
            <div className="text-xs text-slate-400 flex items-center gap-1 truncate">
              <Mail size={12} /> {user.email}
            </div>
          </div>
          <div
            className="ml-auto text-right shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-full border"
            style={{ borderColor: (TIER_COLORS[user.tier] || "#94A3B8") + "66" }}
          >
            <Crown size={13} style={{ color: TIER_COLORS[user.tier] || "#94A3B8" }} />
            <span className="text-xs font-semibold" style={{ color: TIER_COLORS[user.tier] || "#94A3B8" }}>
              {user.tier || "Rookie"}
            </span>
          </div>
        </motion.div>

        {/* referral entry */}
        <button
          data-testid="open-referral-btn"
          onClick={() => navigate("/referral")}
          className="w-full rounded-2xl border border-[#00E676]/30 bg-gradient-to-br from-[#0E2E25] to-[#121721] p-4 flex items-center gap-3 text-left"
        >
          <div className="w-10 h-10 rounded-xl bg-[#00E676]/15 flex items-center justify-center">
            <Users className="text-[#00E676]" size={20} />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold">Referral & Rewards</div>
            <div className="text-xs text-slate-400">
              {user.referral_count || 0} referrals · earn {(user.commission_rate * 100).toFixed(2)}% chain commission
            </div>
          </div>
          <ChevronRight className="ml-auto text-slate-500" size={18} />
        </button>

        {user.role === "admin" && (
          <button
            data-testid="open-admin-btn"
            onClick={() => navigate("/admin")}
            className="w-full rounded-2xl border border-[#3B82F6]/30 bg-gradient-to-br from-[#3B82F6]/15 to-[#121721] p-4 flex items-center gap-3 text-left"
          >
            <div className="w-10 h-10 rounded-xl bg-[#3B82F6]/15 flex items-center justify-center">
              <ShieldAlert className="text-[#3B82F6]" size={20} />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold">Admin Panel</div>
              <div className="text-xs text-slate-400">Manage users, chains, freezes & support</div>
            </div>
            <ChevronRight className="ml-auto text-slate-500" size={18} />
          </button>
        )}

        <div className="grid grid-cols-1 gap-3">
          {stats.map((s) => (
            <div key={s.label} className="rounded-xl border border-[#1E293B] bg-[#121721] p-4 flex items-center gap-3">
              <s.icon className={s.color} size={20} />
              <span className="text-sm text-slate-400 flex-1">{s.label}</span>
              <span className={`font-mono-num font-semibold ${s.color}`}>{s.value}</span>
            </div>
          ))}
        </div>

        <div className="rounded-xl border border-[#1E293B] bg-[#121721] p-4">
          <div className="flex items-center gap-2 text-sm mb-2">
            <Shield size={16} className="text-[#3B82F6]" /> Account Status
          </div>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-slate-500">Trading</span>
              <span className={user.trading_unlocked ? "text-[#00E676]" : "text-[#F59E0B]"}>
                {user.trading_unlocked ? "Unlocked" : "Locked (needs balance)"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Withdrawals</span>
              <span className={user.withdraw_unlocked ? "text-[#00E676]" : "text-[#F59E0B]"}>
                {user.withdraw_unlocked ? "Unlocked" : "Locked (deposit 50)"}
              </span>
            </div>
          </div>
        </div>

        <Button
          data-testid="logout-btn"
          onClick={logout}
          variant="outline"
          className="w-full h-12 border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#3A141A] hover:text-[#FF3B30] rounded-xl bg-transparent"
        >
          <LogOut size={18} className="mr-2" /> Sign Out
        </Button>

        <p className="text-center text-[10px] text-slate-600 pt-2">
          ProCrypto · <a href="/how-to-play" className="text-emerald-400 underline">How to Play</a> · <a href="/game-policy" className="underline">Game policy</a>
        </p>
      </div>
    </div>
  );
}
